import { zodResolver } from "@hookform/resolvers/zod";
import {
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import {
  Alert,
  Button,
  ConfirmationModal,
  TextField,
} from "@platform/design-system";
import { Invite } from "@platform/types";
import { useSignedInUser } from "@platform/user/client";
import { useMutation } from "@tanstack/react-query";
import { JSX, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { useApi } from "@platform/api";

import { AGENT, BROKER } from "../../constants";
import { extractErrorMessage } from "../../utils";
import { submitEvent } from "../../utils/tracking";
import useToastNotificationService from "../../hooks/useToastNotificationService";

const roleMsg = "Role is required";

type InviteVariables = {
  firstName: string;
  lastName: string;
  email: string;
  brokerId: number;
  role?: string;
};

type InviteFormType = {
  firstName: string;
  lastName: string;
  email: string;
  confirmationEmail: string;
  role?: { label: string; value: string };
};

const baseSchema = z.object({
  firstName: z.string().min(1, "First name is required").trim(),
  lastName: z.string().min(1, "Last name is required").trim(),
  email: z.string().email("Invalid email address"),
  confirmationEmail: z.string().min(1, "Email confirmation is required"),
});

const createInviteSchema = (isAdmin: boolean): z.ZodType<InviteFormType> => {
  const schema = isAdmin
    ? baseSchema.extend({
        role: z.object(
          {
            label: z.string().min(1, roleMsg),
            value: z.string().min(1, roleMsg),
          },
          {
            message: roleMsg,
          },
        ),
      })
    : baseSchema;

  return schema.refine((data) => data.email === data.confirmationEmail, {
    message: "Email addresses don't match",
    path: ["confirmationEmail"],
  }) as z.ZodType<InviteFormType>;
};

const ROLES = [
  { label: "User", value: AGENT },
  { label: "Super User", value: BROKER },
];

function InviteDialog({
  open,
  brokerId,
  onSuccess,
  onClose,
}: {
  open: boolean;
  brokerId: number;
  onSuccess: VoidFunction;
  onClose: VoidFunction;
}): JSX.Element {
  const axiosInstance = useApi();
  const { createToastNotification } = useToastNotificationService();
  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const { isAdminOrConsultant: adminOrConsultant } = useSignedInUser();

  const inviteSchema = createInviteSchema(adminOrConsultant);
  type InviteFormData = z.infer<ReturnType<typeof createInviteSchema>> & {
    role?: { label: string; value: string };
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = useForm<InviteFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(inviteSchema as any),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      confirmationEmail: "",
      ...(adminOrConsultant ? { role: { label: "", value: "" } } : {}),
    },
    mode: "onTouched",
  });

  useEffect(() => {
    submitEvent("User Registration Dialog Viewed");
  }, []);

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const {
    mutate,
    isPending,
    error,
    reset: resetMutation,
  } = useMutation<Invite, Error, InviteVariables>({
    mutationFn: (body) =>
      axiosInstance
        .post(
          `/brokerportal/v1/${adminOrConsultant ? "inviteUserOrSuperUser" : "inviteAgent"}`,
          body,
        )
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        .then((res) => res.data),
    onSuccess: (data) => {
      const { firstName, lastName } = data;
      createToastNotification({
        severity: "success",
        message: `Invite successfully sent to ${firstName} ${lastName}`,
      });
      onSuccess();
    },
  });

  const onSubmit = (data: InviteFormData): void => {
    if (error) {
      resetMutation();
    }

    mutate({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email.toLowerCase(),
      ...(adminOrConsultant && { brokerId, role: data.role?.value }),
    });
  };

  const handleClose = (): void => {
    if (isPending) {
      return;
    }
    if (isDirty && !isConfirmationDialogOpen) {
      setConfirmationDialogOpen(true);
      return;
    }
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Add User</DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            component="form"
            flexDirection="column"
            gap={2}
            paddingY={1}
            onSubmit={handleSubmit(onSubmit)}
          >
            <Grid container spacing={3}>
              {adminOrConsultant && (
                <Grid size={12}>
                  <Controller
                    name="role"
                    control={control}
                    render={({ field, fieldState }) => (
                      <Autocomplete
                        {...field}
                        value={field.value || null}
                        onChange={(_, value) => field.onChange(value)}
                        options={ROLES}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="sm"
                            required
                            label="User Role"
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12, md: 6 }}>
                <input
                  autoComplete="off"
                  name="Name"
                  type="text"
                  style={{ display: "none" }}
                />
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      autoComplete="none"
                      id="firstName-input"
                      required
                      label="First name"
                      fullWidth
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      autoComplete="none"
                      id="lastName-input"
                      required
                      label="Last name"
                      fullWidth
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      autoComplete="none"
                      id="email-input"
                      required
                      label="Email"
                      fullWidth
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="confirmationEmail"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      autoComplete="none"
                      id="confirmationEmail-input"
                      required
                      label="Confirm Email"
                      fullWidth
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
              {error && (
                <Grid size={12}>
                  <Alert severity="error">{extractErrorMessage(error)}</Alert>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={isPending}
            onClick={handleClose}
            variant="secondary"
          >
            Close
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isPending}
            disabled={!isValid || isPending}
            onClick={handleSubmit(onSubmit)}
          >
            Send Invite
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title="Confirm Closing"
        description="If you close the form, your changes will be lost. Are you sure you want to proceed?"
        confirmButtonText="Yes"
        cancelButtonText="No"
        open={isConfirmationDialogOpen}
        onCancel={() => setConfirmationDialogOpen(false)}
        onAccept={() => {
          setConfirmationDialogOpen(false);
          handleClose();
        }}
      />
    </>
  );
}

export default InviteDialog;
